import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Network, Search, AlertTriangle } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';

/** Kadry → Przełożeni: kto podpisuje wnioski urlopowe danego pracownika. */
const LeaveSupervisors = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onlyWithout, setOnlyWithout] = useState(false);
  const [query, setQuery] = useState('');
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/kadry/przelozeni');
      setRows(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Nie udało się pobrać listy');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const options = useMemo(
    () => [...rows].sort((a, b) => a.name.localeCompare(b.name, 'pl')),
    [rows]
  );

  const visible = rows.filter((r) => {
    if (onlyWithout && r.source !== 'NONE') return false;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return `${r.name} ${r.email} ${r.departmentName || ''}`.toLowerCase().includes(q);
  });

  const withoutCount = rows.filter((r) => r.source === 'NONE').length;

  const save = async (row, value) => {
    setSavingId(row.userId);
    try {
      await api.put(`/kadry/przelozeni/${row.userId}`, { supervisorId: value ? Number(value) : null });
      toast.success('Zapisano przełożonego');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Nie udało się zapisać');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 text-white flex items-center justify-center shadow-md">
          <Network className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 leading-tight">Przełożeni</h1>
          <p className="text-sm text-gray-500">Kto podpisuje wnioski urlopowe pracownika. Pusto = domyślnie jedyny kierownik działu.</p>
        </div>
      </div>

      {withoutCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 flex items-start gap-3 text-sm text-amber-900">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <span>
            <b>{withoutCount}</b> {withoutCount === 1 ? 'osoba nie ma' : 'osób nie ma'} przełożonego i nie może złożyć wniosku urlopowego.
          </span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Szukaj pracownika lub działu"
            className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg shadow-sm text-sm w-72 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={onlyWithout} onChange={(e) => setOnlyWithout(e.target.checked)}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded" />
          Tylko bez przełożonego
        </label>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-x-auto">
        {loading ? (
          <div className="flex justify-center items-center h-40"><LoadingSpinner size="lg" /></div>
        ) : (
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                {['Pracownik', 'Dział', 'Przełożony teraz', 'Ustaw przełożonego'].map((h) => (
                  <th key={h} className="px-4 py-2 text-left text-[11px] font-semibold tracking-wider text-gray-500 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {visible.map((r) => (
                <tr key={r.userId}>
                  <td className="px-4 py-3 text-sm">
                    <div className="text-gray-900">{r.name}</div>
                    <div className="text-xs text-gray-500">{r.email}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{r.departmentName || '–'}</td>
                  <td className="px-4 py-3 text-sm">
                    {r.source === 'EXPLICIT' && <span className="text-gray-900">{r.effectiveSupervisorName}</span>}
                    {r.source === 'DEPARTMENT' && (
                      <span className="text-gray-700">{r.effectiveSupervisorName} <span className="text-xs text-gray-400">(domyślnie, kierownik działu)</span></span>
                    )}
                    {r.source === 'NONE' && <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">brak</span>}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex items-center gap-2">
                      <select
                        value={r.explicitSupervisorId || ''}
                        disabled={savingId === r.userId}
                        onChange={(e) => save(r, e.target.value)}
                        className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm max-w-xs focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="">
                          {r.suggestedSupervisorName ? `Domyślnie: ${r.suggestedSupervisorName}` : '– brak –'}
                        </option>
                        {options.filter((o) => o.userId !== r.userId).map((o) => (
                          <option key={o.userId} value={o.userId}>{o.name}{o.departmentName ? ` (${o.departmentName})` : ''}</option>
                        ))}
                      </select>
                      {savingId === r.userId && <LoadingSpinner size="sm" />}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default LeaveSupervisors;
